import Cookies from 'js-cookie';
import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { FaPlus, FaX } from 'react-icons/fa6';
import { Button, Form } from 'react-bootstrap';

const defaultCoordinates = [0.0, 0.0]; //These are set to Comal county TX for now.

const LINE_EDIT_COLOR = 'red';
const LINE_IDLE_COLOR = 'grey';

const DEFAULT_LINE_OBJECT = {
  name: 'Road Segment',
  coordinates: [],
  updated: 0,
};

/**
 *
 * @param {{value: {name: string, coordinates: latlng[]}[], onChange: (value: {name: string, coordinates: latlng[]}[]), initialCenter: latlng}}
 * @returns
 */
export default function RoadDefinerMap({ value, onChange, initialCenter, initialZoom, children }) {
  const mapRef = useRef();

  const [selectedLine, setSelectedLine] = useState(-1);
  const [pointPopupOpen, setPointPopupOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState(
    initialCenter
      ? initialCenter
      : [
          Cookies.get('latitude') || defaultCoordinates[0],
          Cookies.get('longitude') || defaultCoordinates[1],
        ],
  );

  useEffect(() => {
    if (mapRef?.current)
      mapRef.current.setView(
        initialCenter
          ? initialCenter
          : [
              Number(Cookies.get('latitude')) || defaultCoordinates[0],
              Number(Cookies.get('latitude')) || defaultCoordinates[1],
            ],
        initialZoom ? initialZoom : 13,
      );
  }, [initialCenter, initialZoom]);

  const addLine = () => {
    let newArr = [{ ...DEFAULT_LINE_OBJECT }];
    if (value && Array.isArray(value) && value.length > 0) {
      newArr = [...value, { ...DEFAULT_LINE_OBJECT }];
    }

    onChange(newArr);
    setSelectedLine(newArr.length - 1);
  };

  const removeLine = (index) => {
    if (!value || !Array.isArray(value)) return;
    let newArr = value.filter((coord, i) => i !== index);
    onChange(newArr);

    if (selectedLine >= newArr.length) setSelectedLine(newArr.length - 1);
  };

  const addPoint = (coordinates) => {
    if (selectedLine < 0 || !value || !Array.isArray(value)) return;

    const newValue = [...value];
    const newArr = [...(value[selectedLine]?.coordinates || []), coordinates];

    newValue[selectedLine].coordinates = newArr;
    onChange(newValue);
  };

  const removePoint = (index) => {
    if (selectedLine < 0 || !value || !Array.isArray(value)) return;

    const newValue = [...value];
    const newArr = [...(value[selectedLine]?.coordinates || []).filter((point, i) => i !== index)];

    newValue[selectedLine].coordinates = newArr;
    onChange(newValue);
  };

  const updatePoint = (index, coordinates) => {
    if (selectedLine < 0 || !value || !Array.isArray(value)) return;

    const newValue = [...value];
    if (newValue[selectedLine]?.coordinates) {
      newValue[selectedLine].coordinates[index] = coordinates;
      newValue[selectedLine].updated++;
    }

    onChange(newValue);
  };

  const renameLine = (index, name) => {
    if (!value || !Array.isArray(value)) return;
    const newValue = [...value];
    if (newValue[index]) {
      newValue[index].name = name;
    }

    onChange(newValue);
  };

  const MapEventListener = () => {
    useMapEvents({
      popupopen: (e) => setPointPopupOpen(true),
      popupclose: (e) => setPointPopupOpen(false),
      click: (e) => {
        if (pointPopupOpen) return;
        const newpoint = { lat: e.latlng.lat, lng: e.latlng.lng };
        addPoint(newpoint);
      },
      zoomend: (e) => console.log('Device Locations: Zoom:', mapRef.current.getZoom()),
    });
  };

  return (
    <div className="d-flex flex-column">
      {/* Controls */}
      <div className="d-flex flex-column gap-2 w-100 w-lg-fit flex-lg-wrap flex-lg-row mb-3">
        <Button
          className="d-flex justify-content-center align-items-center p-3 rounded-3"
          onClick={addLine}
          variant="secondary"
        >
          <FaPlus />
        </Button>
        {value?.map((line, index) => (
          <div
            key={`line-control-${index}-${line.name}`}
            className="bg-primary d-flex justify-content-between align-items-center p-2 rounded-3"
            data-selected={selectedLine === index ? 'true' : 'false'}
            onClick={() => setSelectedLine(index)}
          >
            <Form.Control
              className="bg-transparent border-0"
              value={line.name}
              onChange={(e) => renameLine(index, e.target.value)}
            />
            <Button
              onClick={(e) => {
                e.stopPropagation();
                removeLine(index);
              }}
              variant="secondary"
            >
              <FaX />
            </Button>
          </div>
        ))}
      </div>

      {/* Map */}
      <div className="w-100">
        <MapContainer
          style={{ width: '100%', aspectRatio: '3/2' }}
          ref={mapRef}
          center={mapCenter}
          zoom={13}
        >
          <MapEventListener />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {children}
          {/* Line render */}
          {value?.map((line, index) => {
            if (line?.coordinates?.length > 0)
              return (
                <div key={`line-${index}-${line.updated}`}>
                  <Polyline
                    key={line.name + '' + index + '' + line.updated + '' + selectedLine}
                    positions={line.coordinates}
                    color={selectedLine === index ? LINE_EDIT_COLOR : LINE_IDLE_COLOR}
                  />

                  {/* Point markers */}
                  {line.coordinates.map((point, pIndex) => (
                    <Marker
                      key={index + '' + pIndex + '' + line.updated}
                      position={point}
                      draggable={selectedLine === index}
                      eventHandlers={{
                        dragend: (e) => updatePoint(pIndex, e.target.getLatLng()),
                      }}
                      icon={
                        new L.DivIcon({
                          className: 'custom-x-icon',
                          html: `<div>
                                      <div style="color: black; font-size: 30px; position: absolute; top: -4px; left: -2px;">X</div>
                                      <div style="color: ${
                                        selectedLine === index ? LINE_EDIT_COLOR : LINE_IDLE_COLOR
                                      }; font-size: 24px; position: absolute; top: 0px; left: 0px;">X</div>
                                      </div>`,
                          iconSize: [20, 20],
                          iconAnchor: [10, 16],
                        })
                      }
                      popup
                      interactive
                    >
                      {selectedLine === index && (
                        <Popup closeOnClick closeButton={false} autoClose>
                          <div
                            onClick={() => {
                              setPointPopupOpen(false);
                              removePoint(pIndex);
                            }}
                          >
                            Delete
                          </div>
                        </Popup>
                      )}
                    </Marker>
                  ))}
                </div>
              );
          })}
          ;
        </MapContainer>
      </div>
    </div>
  );
}
